import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import { personalizationService } from '../services/personalizationService';

interface Workspace {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  plan: string;
  max_users: number;
  max_screenshots: number;
  max_storage_gb: number;
  max_ai_tokens: number;
  settings: any;
  personalization: any;
  company_website?: string;
  industry?: string;
  company_size?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: string;
  status: string;
  weekly_capacity: number;
  joined_at: string;
  last_active_at?: string;
  name?: string;
  email?: string;
  avatar?: string;
}

interface WorkspaceContextType {
  workspace: Workspace | null;
  members: WorkspaceMember[];
  loading: boolean;
  error: string | null;
  refreshWorkspace: () => Promise<void>;
  refreshMembers: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state: authState } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadWorkspace = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getCurrentWorkspace();
      setWorkspace(data);
      console.log('🏢 WorkspaceContext: Loaded workspace:', data);
      
      // 🔄 Notify personalizationService of workspace change
      if (data?.id) {
        personalizationService.setCurrentWorkspace(data.id);
        console.log('🎨 WorkspaceContext: Set personalization workspace to:', data.id);
      }
    } catch (err: any) {
      console.error('WorkspaceContext: Failed to load workspace:', err);
      setError(err.message || 'Failed to load workspace');
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      const data = await apiService.getWorkspaceMembers();
      setMembers(data);
      console.log('👥 WorkspaceContext: Loaded members:', data.length);
    } catch (err: any) {
      console.error('WorkspaceContext: Failed to load members:', err);
      setMembers([]);
    }
  };

  useEffect(() => {
    // 🛡️ AUTH GUARD: Only load workspace data when authenticated
    if (!authState.isAuthenticated) {
      console.log('⏸️ WorkspaceContext: Skipping load (not authenticated)');
      setLoading(false);
      return;
    }

    console.log('✅ WorkspaceContext: User authenticated, loading workspace data');
    loadWorkspace();
    loadMembers();
  }, [authState.isAuthenticated]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        members,
        loading,
        error,
        refreshWorkspace: loadWorkspace,
        refreshMembers: loadMembers
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};
